// src/components/HybridRecentUploads.js
import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';

const SHAREPOINT_SITE_ID = 'mcmastersu.sharepoint.com,23c5a09e-4be1-4e77-8dca-0ea82ab36649,b3d092bd-48df-4bb3-b730-2238382489f7';
const RECENT_UPLOAD_LIMIT = 3;
const GRAPH_PAGE_SIZE = 25;
const UPLOAD_FOLDER_PATHS = [
  '/Shared Documents/publicUploads',
  '/publicUploads',
  'publicUploads'
];
const EXCLUDED_UPLOAD_NAMES = new Set(['new releases']);

function HybridRecentUploads() {
  const { instance, accounts } = useMsal();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [fileCount, setFileCount] = useState(0);

  const isAuthenticated = accounts.length > 0;

  const isVisibleUpload = (file) => {
    if (!file?.name) return false;

    const normalizedName = file.name.trim().toLowerCase();
    if (EXCLUDED_UPLOAD_NAMES.has(normalizedName)) return false;

    // Graph returns folders alongside files unless we remove them client-side.
    if (file.folder) return false;

    return true;
  };

  const normalizeUploads = useCallback((items = []) => (
    items
      .filter(isVisibleUpload)
      .map((file) => ({
        name: file.name,
        size: Number(file.size) || 0,
        uploadDate: file.uploadDate || file.lastModifiedDateTime,
        uploader: file.uploader || file.createdBy?.user?.displayName || 'Unknown',
        webUrl: file.webUrl || ''
      }))
      .filter((file) => file.uploadDate)
      .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
      .slice(0, RECENT_UPLOAD_LIMIT)
  ), []);

  const fetchSharePointUploads = useCallback(async (accessToken, selectFields = 'name,lastModifiedDateTime,size,webUrl,createdBy,folder,file') => {
    let lastError = null;

    for (const folderPath of UPLOAD_FOLDER_PATHS) {
      try {
        const graphUrl = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_SITE_ID}/drive/root:${folderPath}:/children?$orderby=lastModifiedDateTime desc&$top=${GRAPH_PAGE_SIZE}&$select=${selectFields}`;
        const graphResponse = await fetch(graphUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!graphResponse.ok) {
          lastError = new Error(`SharePoint path ${folderPath} returned ${graphResponse.status}`);
          continue;
        }

        const data = await graphResponse.json();
        const normalizedUploads = normalizeUploads(data.value || []);

        return {
          uploads: normalizedUploads,
          sourcePath: folderPath,
          rawCount: data.value?.length || 0
        };
      } catch (pathError) {
        lastError = pathError;
      }
    }

    throw lastError || new Error('Cannot access SharePoint uploads folder');
  }, [normalizeUploads]);

  // Load uploads from static JSON file (public access)
  const loadPublicUploads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/recent-uploads.json', { cache: 'no-store' });

      if (!response.ok) {
        throw new Error('No recent uploads data available');
      }

      const data = await response.json();
      setUploads(normalizeUploads(data.uploads || []));
      setLastUpdated(data.lastUpdated || null);
    } catch (err) {
      setError('Unable to load recent uploads');
      console.error('Error loading uploads:', err);
    } finally {
      setLoading(false);
    }
  }, [normalizeUploads]);

  // Fetch fresh data from SharePoint and update JSON file (authenticated users only)
  const updateUploadsFromSharePoint = useCallback(async (isAutoUpdate = false) => {
    if (!isAuthenticated) return;

    setUpdating(true);
    setError(null);

    try {
      const tokenRequest = {
        scopes: ['User.Read', 'Sites.Read.All'],
        account: accounts[0]
      };

      let response;
      try {
        response = await instance.acquireTokenSilent(tokenRequest);
      } catch (silentError) {
        console.log('Silent token acquisition failed, trying interactive:', silentError);

        try {
          response = await instance.acquireTokenPopup(tokenRequest);
        } catch (popupError) {
          console.log('Popup failed, trying redirect with consent prompt:', popupError);
          await instance.acquireTokenRedirect({
            ...tokenRequest,
            prompt: 'consent'
          });
          return;
        }
      }

      const { uploads: freshUploads, sourcePath } = await fetchSharePointUploads(response.accessToken);

      const updatedData = {
        uploads: freshUploads,
        lastUpdated: new Date().toISOString(),
        updatedBy: accounts[0].name,
        sourcePath
      };

      setUploads(freshUploads);
      setLastUpdated(updatedData.lastUpdated);
      setLastChecked(freshUploads[0]?.uploadDate || null);
      setFileCount(freshUploads.length);

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';
        const updateResponse = await fetch(`${backendUrl}/update-uploads`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatedData)
        });

        if (updateResponse.ok) {
          if (!isAutoUpdate) {
            alert(`Updated. ${freshUploads.length} recent uploads are now visible to everyone.`);
          }
        } else if (!isAutoUpdate) {
          alert(`Display updated, but could not save for other users. Backend status: ${updateResponse.status}`);
        }
      } catch (backendError) {
        console.warn('Backend not available:', backendError);
        if (!isAutoUpdate) {
          alert(`Display updated, but could not save for other users. Backend offline: ${backendError.message}`);
        }
      }
    } catch (updateError) {
      console.error('Error updating uploads:', updateError);

      if (updateError.message.includes('consent')) {
        setError('Permission required. Please contact your administrator to grant consent for SharePoint access.');
      } else if (updateError.message.includes('404')) {
        setError('publicUploads folder not found. Please check the folder exists and try again.');
      } else {
        setError(`Failed to update: ${updateError.message}`);
      }
    } finally {
      setUpdating(false);
    }
  }, [accounts, fetchSharePointUploads, instance, isAuthenticated]);

  useEffect(() => {
    loadPublicUploads();
  }, [loadPublicUploads]);

  const checkForUpdates = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const tokenRequest = {
        scopes: ['User.Read', 'Sites.Read.All'],
        account: accounts[0]
      };

      const response = await instance.acquireTokenSilent(tokenRequest);
      const { uploads: latestUploads } = await fetchSharePointUploads(response.accessToken, 'name,lastModifiedDateTime,size,webUrl,createdBy,folder,file');
      const latestUploadDate = latestUploads[0]?.uploadDate;

      if (!latestUploadDate) return;

      if (!lastChecked || new Date(latestUploadDate) > new Date(lastChecked) || latestUploads.length !== fileCount) {
        await updateUploadsFromSharePoint(true);
      }
    } catch (checkError) {
      console.error('Error checking for updates:', checkError);
    }
  }, [accounts, fetchSharePointUploads, fileCount, instance, isAuthenticated, lastChecked, updateUploadsFromSharePoint]);

  // Set up smart polling for automatic updates
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const pollInterval = setInterval(checkForUpdates, 2 * 60 * 1000);
    const initialCheck = setTimeout(checkForUpdates, 5000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(initialCheck);
    };
  }, [checkForUpdates, isAuthenticated]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown date';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toUpperCase();
    return extension && extension !== fileName.toUpperCase() ? extension : 'FILE';
  };

  return (
    <div className="recent-uploads">
      <div className="recent-uploads-header">
        <h3>Recent Community Uploads</h3>
        <div className="header-controls">
          {lastUpdated && (
            <span className="last-updated">
              Updated {formatDate(lastUpdated)}
            </span>
          )}
          <div className="button-group">
            <button
              onClick={loadPublicUploads}
              className="refresh-button"
              disabled={loading}
              title="Reload recent uploads"
              type="button"
            >
              {loading ? 'Loading' : 'Reload'}
            </button>
            {isAuthenticated && (
              <button
                onClick={() => updateUploadsFromSharePoint(false)}
                className="update-button"
                disabled={updating}
                title="Refresh from SharePoint"
                type="button"
              >
                {updating ? 'Updating...' : 'Refresh Now'}
              </button>
            )}
          </div>
        </div>
      </div>

      {(loading || updating) && (
        <div className="uploads-loading">
          <div className="loading-spinner" />
          <span>
            {updating ? 'Fetching fresh data from SharePoint...' : 'Loading recent uploads...'}
          </span>
        </div>
      )}

      {error && (
        <div className="uploads-error">
          <p>{error}</p>
          <button onClick={loadPublicUploads} className="retry-button" type="button">
            Try Again
          </button>
        </div>
      )}

      {!loading && !updating && !error && uploads.length === 0 && (
        <div className="no-uploads">
          <p>No recent uploads to display</p>
          {isAuthenticated && (
            <button onClick={() => updateUploadsFromSharePoint(false)} className="update-button" type="button">
              Refresh Now
            </button>
          )}
        </div>
      )}

      {!loading && !updating && !error && uploads.length > 0 && (
        <div className="uploads-list">
          {uploads.map((file) => (
            <div key={`${file.name}-${file.uploadDate}`} className="upload-item">
              <div className="file-info">
                <div className="file-icon" aria-hidden="true">{getFileIcon(file.name)}</div>
                <div className="file-details">
                  <div className="file-name" title={file.name}>
                    {file.name.length > 30 ? `${file.name.substring(0, 30)}...` : file.name}
                  </div>
                  <div className="file-meta">
                    <span className="file-size">{formatFileSize(file.size)}</span>
                    <span className="file-date">{formatDate(file.uploadDate)}</span>
                    <span className="file-uploader">by {file.uploader}</span>
                  </div>
                </div>
              </div>
              {file.webUrl && (
                <a
                  href={file.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-file-link"
                  title="View file"
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {isAuthenticated && !updating && (
        <div className="update-help">
          <small>
            Auto-updating: the list checks for new uploads every 2 minutes. Click "Refresh Now" for an immediate update.
          </small>
        </div>
      )}

      {!isAuthenticated && (
        <div className="auth-info">
          <small>
            This list updates automatically when a signed-in staff member refreshes SharePoint uploads.
          </small>
        </div>
      )}
    </div>
  );
}

export default HybridRecentUploads;
